// DensityHeatmap — 7-row (Mon-first) × N-column heatmap, one column
// per ISO week across the selected range (Quarter ≈ 13w, 6m ≈ 26w,
// 12m ≈ 52w; spec §9.2). A 16px month-label band sits above the grid,
// one label per first-of-month aligned to its column. Cells are
// DensityHeatmapCell, bucketed by activity date; clicking a cell sets
// the selected/focus date which drives the agenda. Today + selected
// flags pass through to each cell (rings stack there).
//
// Wrapped in a captioned section with a split legend (Volume ramp ·
// Severity overlays · Today ring). The brand contract is mechanical:
// severity REPLACES the volume tone in DensityHeatmapCell (a red day
// renders sev-red instead of d-N, not in addition); Today is the only
// ring overlay; orthogonal slots — fill / overlay / ring — keep the
// legend honest.
//
// The {from,to} span the page fetches MUST be derived from the same
// getDensitySpan helper this component uses, otherwise the cell grid
// can render days outside the API window. The helper is shared via
// utils/schedule.

import { useMemo } from "react";
import DensityHeatmapCell from "./DensityHeatmapCell";
import LegendSwatch from "./legend/LegendSwatch";
import type { Activity } from "../../../types/schedule";
import {
  getDensitySpan,
  RANGE_WEEKS,
  type DensityRange,
} from "../../../utils/schedule";

interface DensityHeatmapProps {
  range: DensityRange;
  // ISO date the span starts from (the page derives this from URL state).
  anchor: string;
  activities: Activity[];
  selectedDate: string | null;
  today: Date;
  onSelectDate: (iso: string) => void;
}

const DAY_MS = 86_400_000;

function isoOf(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;
}

// Human-readable label for a cell's native browser tooltip — e.g.
// "Wed 6 May 2026". Reuses the same long-form date shape the agenda
// + DayPopover headers use so a hover and a click read the same date.
function formatCellTitle(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function DensityHeatmap({
  range,
  anchor,
  activities,
  selectedDate,
  today,
  onSelectDate,
}: DensityHeatmapProps) {
  const todayIso = isoOf(today);
  const weeks = RANGE_WEEKS[range];

  // Columns: each is a Monday-aligned ISO week; rows are Mon..Sun.
  // Start anchor comes from the shared span helper so the API window
  // and the rendered grid cover the same calendar surface.
  const columns = useMemo(() => {
    const { from } = getDensitySpan(anchor, range);
    const start = new Date(`${from}T00:00:00Z`);
    return Array.from({ length: weeks }, (_, w) => {
      const colStart = new Date(start.getTime() + w * 7 * DAY_MS);
      const days = Array.from({ length: 7 }, (_, d) =>
        isoOf(new Date(colStart.getTime() + d * DAY_MS)),
      );
      return { weekIndex: w, days };
    });
  }, [anchor, range, weeks]);

  const byDate = useMemo(() => {
    const map = new Map<string, Activity[]>();
    for (const a of activities) {
      const list = map.get(a.date);
      if (list) list.push(a);
      else map.set(a.date, [a]);
    }
    return map;
  }, [activities]);

  // One month label per column whose Monday-week contains a
  // first-of-month, aligned to that column.
  const monthLabels = useMemo(() => {
    const out: { weekIndex: number; label: string }[] = [];
    let lastMonth = "";
    for (const col of columns) {
      for (const iso of col.days) {
        const m = iso.slice(0, 7);
        if (m !== lastMonth) {
          lastMonth = m;
          const d = new Date(`${iso}T00:00:00Z`);
          out.push({
            weekIndex: col.weekIndex,
            label: d.toLocaleDateString("en-AU", {
              month: "short",
              year: "numeric",
              timeZone: "UTC",
            }),
          });
          break;
        }
      }
    }
    return out;
  }, [columns]);

  return (
    <section
      data-testid="density-section"
      className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
    >
      <header className="mb-3">
        <h2 className="text-h4 text-slate-900 dark:text-slate-100">
          Activity density
        </h2>
        <p className="text-caption text-slate-500 dark:text-slate-400">
          Cell intensity = activity count · severity (red/amber)
          replaces the count tone · today is ringed
        </p>
      </header>

      <div className="overflow-x-auto">
        <div
          data-testid="heatmap-month-band"
          className="mb-1 flex h-4 gap-[3px]"
          style={{ minWidth: `${weeks * 16}px` }}
        >
          {columns.map((col) => {
            const label = monthLabels.find(
              (m) => m.weekIndex === col.weekIndex,
            );
            return (
              <div
                key={col.weekIndex}
                className="flex-1"
                style={{ minWidth: 13 }}
              >
                {label && (
                  <span
                    data-testid={`heatmap-month-${col.weekIndex}`}
                    className="whitespace-nowrap text-[10px] font-medium text-slate-500 dark:text-slate-400"
                  >
                    {label.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-[3px]" style={{ minWidth: `${weeks * 16}px` }}>
          {columns.map((col) => (
            <div
              key={col.weekIndex}
              className="flex flex-1 flex-col gap-[3px]"
              style={{ minWidth: 13 }}
            >
              {col.days.map((iso) => (
                <div key={iso} data-testid={`heatmap-cell-${iso}`}>
                  <DensityHeatmapCell
                    activities={byDate.get(iso) ?? []}
                    isToday={iso === todayIso}
                    isSelected={iso === selectedDate}
                    onClick={() => onSelectDate(iso)}
                    title={formatCellTitle(iso)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <footer
        data-testid="density-legend"
        className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-caption text-slate-500 dark:text-slate-400"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium uppercase tracking-wide">
            Volume
          </span>
          <LegendSwatch
            testId="density-legend-volume-1"
            label="1"
            shape="block"
            tone="volume-1"
          />
          <LegendSwatch
            testId="density-legend-volume-2"
            label="2"
            shape="block"
            tone="volume-2"
          />
          <LegendSwatch
            testId="density-legend-volume-3"
            label="3"
            shape="block"
            tone="volume-3"
          />
          <LegendSwatch
            testId="density-legend-volume-4"
            label="4+"
            shape="block"
            tone="volume-4"
          />
        </div>
        <div className="flex items-center gap-3">
          <LegendSwatch
            testId="density-legend-severity-amber"
            label="Attention"
            shape="block"
            tone="amber"
          />
          <LegendSwatch
            testId="density-legend-severity-red"
            label="Overdue"
            shape="block"
            tone="red"
          />
          <LegendSwatch
            testId="density-legend-today"
            label="Today"
            shape="ring"
            tone="brand"
          />
        </div>
      </footer>
    </section>
  );
}
